/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DropdownMC from '../../../../../../../../components/DropdownMC';
import IconMC from '../../../../../../../../components/IconMC';
import RetailerAuthMethodModel from '../../../../../../../../models/RetailerAuthMethodModel';
import { encryptRetailerAuthMethodDataForTransport } from '../../../../../../../../../util/retailerAuthMethodDataTransport';
import { showtoast } from '../../../../../../../../../util/toast';
import { RETAILER_AUTH_METHOD_NONE_ID } from '../../hooks/globalInstructionToolsModal/retailerApiRequestPayload';

export const RETAILER_AUTH_METHOD_TYPES = {
  BASIC: 'basic-auth',
  BEARER: 'bearer-auth',
  API_KEY: 'api-key'
};

const AUTH_METHOD_TYPE_SET = new Set(Object.values(RETAILER_AUTH_METHOD_TYPES));

export const emptyRetailerAuthMethodsDraft = () => ({
  name: '',
  type: RETAILER_AUTH_METHOD_TYPES.BASIC,
  user: '',
  password: '',
  bearerToken: '',
  apiKeyHeaderName: '',
  apiKeyValue: ''
});

const tryBuildRetailerAuthMethodPayload = (draft) => {
  const name = (draft.name || '').trim();
  if (!name) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.name_required' };
  if (!AUTH_METHOD_TYPE_SET.has(draft.type)) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.type_invalid' };

  if (draft.type === RETAILER_AUTH_METHOD_TYPES.BASIC) {
    const user = (draft.user || '').trim();
    const password = (draft.password || '').trim();
    if (!user) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.user_required' };
    if (!password) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.password_required' };
    return { ok: true, payload: { type: draft.type, name, data: { user, password } } };
  }

  if (draft.type === RETAILER_AUTH_METHOD_TYPES.BEARER) {
    const bearerToken = (draft.bearerToken || '').trim();
    if (!bearerToken) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.bearer_token_required' };
    return { ok: true, payload: { type: draft.type, name, data: { 'bearer-token': bearerToken } } };
  }

  const apiKeyHeaderName = (draft.apiKeyHeaderName || '').trim();
  const apiKeyValue = (draft.apiKeyValue || '').trim();
  if (!apiKeyHeaderName) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.api_key_name_required' };
  if (!apiKeyValue) return { ok: false, errorKey: 'tools.retailer_auth_methods_errors.api_key_value_required' };
  return {
    ok: true,
    payload: {
      type: draft.type,
      name,
      data: { header_name: apiKeyHeaderName, api_key: apiKeyValue }
    }
  };
};

const floatingFieldClass =
  'tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-[#F7F8FD] tw-px-3 tw-py-3 tw-text-sm tw-text-gray-800 tw-outline-none focus:tw-border-gray-300';

const RetailerAuthMethodsSection = ({
  t,
  selectedAuthMethodId,
  setSelectedAuthMethodId,
  retailerAuthMethodsDraft,
  setRetailerAuthMethodsDraft,
  authMethodsFormMode,
  setAuthMethodsFormMode,
  embedded = false
}) => {
  const [authMethods, setAuthMethods] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [savingAuth, setSavingAuth] = useState(false);

  const typeOptions = useMemo(
    () => [
      { id: RETAILER_AUTH_METHOD_TYPES.BASIC, label: t('tools.retailer_auth_methods_type_basic') },
      { id: RETAILER_AUTH_METHOD_TYPES.BEARER, label: t('tools.retailer_auth_methods_type_bearer') },
      { id: RETAILER_AUTH_METHOD_TYPES.API_KEY, label: t('tools.retailer_auth_methods_type_api_key') }
    ],
    [t]
  );

  const authMethodOptions = useMemo(
    () => [
      { id: RETAILER_AUTH_METHOD_NONE_ID, label: t('tools.retailer_auth_methods_none_option') },
      ...authMethods.map((item) => ({ id: String(item.id), label: item.name }))
    ],
    [authMethods, t]
  );

  const usesNoAuth = selectedAuthMethodId === RETAILER_AUTH_METHOD_NONE_ID;
  const usesBasicAuth = retailerAuthMethodsDraft.type === RETAILER_AUTH_METHOD_TYPES.BASIC;
  const usesBearerAuth = retailerAuthMethodsDraft.type === RETAILER_AUTH_METHOD_TYPES.BEARER;
  const usesApiKeyAuth = retailerAuthMethodsDraft.type === RETAILER_AUTH_METHOD_TYPES.API_KEY;

  const typeHintKey = useMemo(() => {
    if (usesBasicAuth) return 'tools.retailer_auth_methods_type_basic_hint';
    if (usesBearerAuth) return 'tools.retailer_auth_methods_type_bearer_hint';
    if (usesApiKeyAuth) return 'tools.retailer_auth_methods_type_api_key_hint';
    return null;
  }, [usesApiKeyAuth, usesBasicAuth, usesBearerAuth]);

  const patchDraft = useCallback((partial) => {
    setRetailerAuthMethodsDraft((prev) => ({ ...prev, ...partial }));
  }, [setRetailerAuthMethodsDraft]);

  const loadAuthMethods = useCallback(async () => {
    setLoadingList(true);
    try {
      const response = await RetailerAuthMethodModel.get();
      const list = response?.data ?? [];
      setAuthMethods(Array.isArray(list) ? list : []);
    } catch (_e) {
      setAuthMethods([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadAuthMethods();
  }, [loadAuthMethods]);

  const handleCreateAuthMethod = useCallback(async () => {
    const result = tryBuildRetailerAuthMethodPayload(retailerAuthMethodsDraft);
    if (!result.ok) {
      showtoast(t(result.errorKey));
      return;
    }

    setSavingAuth(true);
    try {
      const payload = {
        ...result.payload,
        data: await encryptRetailerAuthMethodDataForTransport(result.payload.data)
      };
      const created = await RetailerAuthMethodModel.save(payload);
      const authMethodId = created?.id ?? null;
      if (!authMethodId) {
        showtoast(t('tools.retailer_auth_methods_errors.create_failed'));
        return;
      }
      setSelectedAuthMethodId(String(authMethodId));
      setAuthMethodsFormMode('select');
      await loadAuthMethods();
    } catch (error) {
      showtoast(error?.message || t('tools.retailer_auth_methods_errors.create_failed'));
    } finally {
      setSavingAuth(false);
    }
  }, [
    loadAuthMethods,
    retailerAuthMethodsDraft,
    setAuthMethodsFormMode,
    setSelectedAuthMethodId,
    t
  ]);

  const wrapperClass = embedded
    ? 'tw-flex tw-flex-col tw-gap-4 tw-pt-1 tw-border-t tw-border-gray-100'
    : 'tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-4 tw-flex tw-flex-col tw-gap-4';

  return (
    <div className={wrapperClass}>
      <div>
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">
          {t('tools.retailer_auth_methods_title')}
        </h3>
        <p className="tw-text-xs tw-text-gray-500">
          {t('tools.retailer_api_request_auth_section_description')}
        </p>
      </div>

      {authMethodsFormMode === 'select' ? (
        <>
          <DropdownMC
            id="retailer_auth_method_id"
            name="retailer_auth_method_id"
            label={t('tools.retailer_auth_methods_select_label')}
            placeholder={loadingList ? t('tools.retailer_auth_methods_loading') : t('tools.select_placeholder')}
            options={authMethodOptions}
            onSelect={setSelectedAuthMethodId}
            defaultValue={selectedAuthMethodId}
            useFloatingLabel
            disabled={loadingList}
          />

          {usesNoAuth && (
            <p className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2">
              {t('tools.retailer_api_request_auth_mode_none_hint')}
            </p>
          )}

          {!usesNoAuth && !loadingList && authMethods.length === 0 && (
            <p className="tw-text-sm tw-text-gray-500">
              {t('tools.retailer_auth_methods_empty_list')}
            </p>
          )}

          <button
            type="button"
            className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-blue-600"
            onClick={() => {
              setAuthMethodsFormMode('create');
              setRetailerAuthMethodsDraft(emptyRetailerAuthMethodsDraft());
            }}
          >
            <IconMC name="add-outline" size="sm" />
            {t('tools.retailer_auth_methods_create_new')}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-600"
            onClick={() => setAuthMethodsFormMode('select')}
          >
            <IconMC name="arrow-back-outline" size="sm" />
            {t('tools.retailer_auth_methods_back_to_list')}
          </button>

          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_name">
                {t('tools.retailer_auth_methods_name_label')}
              </label>
              <input
                id="retailer_auth_methods_name"
                name="retailer_auth_methods_name"
                type="text"
                className={floatingFieldClass}
                value={retailerAuthMethodsDraft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
                placeholder={t('tools.retailer_auth_methods_name_placeholder')}
                autoComplete="off"
              />
            </div>

            <DropdownMC
              id="retailer_auth_methods_type"
              name="retailer_auth_methods_type"
              label={t('tools.retailer_auth_methods_type_label')}
              options={typeOptions}
              onSelect={(type) => patchDraft({ type })}
              defaultValue={retailerAuthMethodsDraft.type}
              useFloatingLabel
            />

            {typeHintKey && (
              <p className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2">
                {t(typeHintKey)}
              </p>
            )}

            {usesBasicAuth && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_user">
                    {t('tools.retailer_auth_methods_user_label')}
                  </label>
                  <input
                    id="retailer_auth_methods_user"
                    type="text"
                    className={floatingFieldClass}
                    value={retailerAuthMethodsDraft.user}
                    onChange={(e) => patchDraft({ user: e.target.value })}
                    autoComplete="off"
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_password">
                    {t('tools.retailer_auth_methods_password_label')}
                  </label>
                  <input
                    id="retailer_auth_methods_password"
                    type="password"
                    className={floatingFieldClass}
                    value={retailerAuthMethodsDraft.password}
                    onChange={(e) => patchDraft({ password: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}

            {usesBearerAuth && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_bearer_token">
                  {t('tools.retailer_auth_methods_bearer_token_label')}
                </label>
                <input
                  id="retailer_auth_methods_bearer_token"
                  name="retailer_auth_methods_bearer_token"
                  type="password"
                  className={floatingFieldClass}
                  value={retailerAuthMethodsDraft.bearerToken}
                  onChange={(e) => patchDraft({ bearerToken: e.target.value })}
                  autoComplete="new-password"
                  placeholder={t('tools.retailer_auth_methods_bearer_token_placeholder')}
                />
              </div>
            )}

            {usesApiKeyAuth && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_api_key_name">
                    {t('tools.retailer_auth_methods_api_key_name_label')}
                  </label>
                  <input
                    id="retailer_auth_methods_api_key_name"
                    name="retailer_auth_methods_api_key_name"
                    type="text"
                    className={floatingFieldClass}
                    value={retailerAuthMethodsDraft.apiKeyHeaderName}
                    onChange={(e) => patchDraft({ apiKeyHeaderName: e.target.value })}
                    autoComplete="off"
                    placeholder={t('tools.retailer_auth_methods_api_key_name_placeholder')}
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_auth_methods_api_key_value">
                    {t('tools.retailer_auth_methods_api_key_value_label')}
                  </label>
                  <input
                    id="retailer_auth_methods_api_key_value"
                    name="retailer_auth_methods_api_key_value"
                    type="password"
                    className={floatingFieldClass}
                    value={retailerAuthMethodsDraft.apiKeyValue}
                    onChange={(e) => patchDraft({ apiKeyValue: e.target.value })}
                    autoComplete="new-password"
                    placeholder={t('tools.retailer_auth_methods_api_key_value_placeholder')}
                  />
                </div>
              </>
            )}

            <button
              type="button"
              className="tw-text-sm tw-font-medium tw-text-blue-600 disabled:tw-opacity-50"
              disabled={savingAuth}
              onClick={handleCreateAuthMethod}
            >
              {t('tools.retailer_auth_methods_save_auth')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RetailerAuthMethodsSection;
